import React, { useMemo } from "react";
import { Box, Text } from "grommet";
import { Checkmark, Close } from "grommet-icons";
import { useTranslation } from "react-i18next";

export default function CheckinFlowTimeline({
  config,
  activeSection,
  horizontal,
}) {
  const { t } = useTranslation();

  const PRECHECKIN_STEPS = useMemo(
    () => [
      {
        key: "guests",
        emoji: "👤",
        label: t("checkinFlow.timeline.stepGuests"),
        alwaysOn: true,
      },
      {
        key: "arrivalTime",
        emoji: "⏱️",
        label: t("checkinFlow.timeline.stepArrival"),
        alwaysOn: true,
      },
      {
        key: "offerEarlyCheckin",
        emoji: "🛏️",
        label: t("checkinFlow.timeline.stepEarlyCheckin"),
      },
      {
        key: "requestPhotoID",
        emoji: "📷",
        label: t("checkinFlow.timeline.stepPhotoId"),
      },
      {
        key: "aiPreCheckEnabled",
        emoji: "🤖",
        label: t("checkinFlow.timeline.stepAiPreCheck"),
      },
      {
        key: "requestCardImprint",
        emoji: "💳",
        label: t("checkinFlow.timeline.stepDeposit"),
      },
      {
        key: "registrationEnabled",
        emoji: "📝",
        label: t("checkinFlow.timeline.stepRegistration"),
      },
      {
        key: "webrezproAgreement",
        emoji: "📄",
        label: t("checkinFlow.timeline.stepAgreement"),
      },
    ],
    [t],
  );

  const SECTIONS = useMemo(
    () => [
      {
        id: "precheckin",
        label: t("checkinFlow.timeline.labelPreCheckin"),
        title: t("checkinFlow.timeline.titlePreCheckin"),
        description: t("checkinFlow.timeline.descPreCheckin"),
        getStatus: (cfg) => ({
          enabled: cfg.onlineCheckinEnabled,
          steps: cfg.onlineCheckinEnabled
            ? PRECHECKIN_STEPS.filter((s) => s.alwaysOn || cfg[s.key])
            : [],
        }),
      },
      {
        id: "reminder",
        label: t("checkinFlow.timeline.labelReminder"),
        title: t("checkinFlow.timeline.titleReminder"),
        description: t("checkinFlow.timeline.descReminder"),
        getStatus: (cfg) => ({
          enabled: cfg.reminderEnabled,
        }),
      },
      {
        id: "approval",
        label: t("checkinFlow.timeline.labelApproval"),
        title: t("checkinFlow.timeline.titleApproval"),
        description: t("checkinFlow.timeline.descApproval"),
        getStatus: (cfg) => ({
          enabled: cfg.onlineCheckinEnabled,
          detail:
            cfg.checkinApproval?.autoApprove === "always"
              ? t("checkinFlow.timeline.statusAlways")
              : cfg.checkinApproval?.autoApprove === "aiPositive"
                ? t("checkinFlow.timeline.statusAiPreCheck")
                : cfg.checkinApproval?.autoApprove === "webrezproAndAi"
                  ? t("checkinFlow.timeline.statusWrpAi")
                  : cfg.checkinApproval?.autoApprove === "webrezproAgreement"
                    ? t("checkinFlow.timeline.statusWrp")
                    : t("checkinFlow.timeline.statusManual"),
        }),
      },
      {
        id: "access",
        hidden: cfg => cfg.serviceType === "messaging",
        label: t("checkinFlow.timeline.labelAccess"),
        title: t("checkinFlow.timeline.titleAccess"),
        description: t("checkinFlow.timeline.descAccess"),
        getStatus: (cfg) => {
          const parts = [];
          if (cfg.autoSendWalkinTime) parts.push(cfg.autoSendWalkinTime);
          if (cfg.autoSendEnabled)
            parts.push(cfg.autoSendWalkinTime ? "+ PMS" : "PMS");
          return {
            enabled: true,
            detail: parts.join(" ") || t("checkinFlow.timeline.statusManual"),
          };
        },
      },
      {
        id: "postcheckin",
        label: t("checkinFlow.timeline.labelPost"),
        title: t("checkinFlow.timeline.titlePost"),
        description: t("checkinFlow.timeline.descPost"),
        hidden: (cfg) => cfg.serviceType === "messaging" || cfg.lockType === "oracode",
        getStatus: (cfg) => ({
          enabled: cfg.postCheckinMessageEnabled,
        }),
      },
    ],
    [t, PRECHECKIN_STEPS],
  );
  if (horizontal) {
    const activeSections = SECTIONS.filter(
      (section) =>
        !(section.hidden && section.hidden(config)) &&
        section.getStatus(config).enabled,
    );

    return (
      <Box
        className="checkin-flow-timeline-horizontal"
        direction="row"
        align="center"
        justify="center"
        pad={{ vertical: "small", horizontal: "medium" }}
        gap="none"
        overflow={{ horizontal: "auto" }}
      >
        {activeSections.map((section, index) => {
          const status = section.getStatus(config);
          const isActive = activeSection === section.id;
          const isLast = index === activeSections.length - 1;

          return (
            <React.Fragment key={section.id}>
              <Box
                align="center"
                flex={false}
                onClick={() => {
                  const el = document.getElementById(`section-${section.id}`);
                  if (el)
                    el.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                }}
                style={{ cursor: "pointer", minWidth: 90 }}
                pad={{ horizontal: "xsmall", vertical: "xsmall" }}
                hoverIndicator={{ color: "light-2" }}
                round="xsmall"
              >
                <Box
                  width="24px"
                  height="24px"
                  round="full"
                  background={status.enabled ? "status-ok" : "light-5"}
                  justify="center"
                  align="center"
                  border={
                    isActive
                      ? { color: "brand", size: "2px" }
                      : {
                          color: status.enabled ? "status-ok" : "light-5",
                          size: "2px",
                        }
                  }
                  style={{ flexShrink: 0 }}
                >
                  {status.enabled ? (
                    <Checkmark size="small" color="white" />
                  ) : (
                    <Close size="10px" color="dark-5" />
                  )}
                </Box>
                <Text
                  size="xsmall"
                  color={isActive ? "brand" : "dark-4"}
                  weight="bold"
                  margin={{ top: "4px" }}
                  textAlign="center"
                >
                  {section.label}
                </Text>
                <Text
                  size="xsmall"
                  weight={isActive ? "bold" : 600}
                  color={isActive ? "brand" : "dark-1"}
                  textAlign="center"
                >
                  {section.title}
                </Text>
                {status.detail && (
                  <Text size="xsmall" color="dark-4" textAlign="center">
                    {status.detail}
                  </Text>
                )}
                {status.steps?.length > 0 && (
                  <Box
                    direction="row"
                    wrap
                    gap="2px"
                    margin={{ top: "2px" }}
                    justify="center"
                  >
                    {status.steps.map((s) => (
                      <Text key={s.key} size="small" title={s.label}>
                        {s.emoji}
                      </Text>
                    ))}
                  </Box>
                )}
              </Box>
              {!isLast && (
                <Box
                  flex
                  direction="row"
                  align="center"
                  alignSelf="center"
                  style={{ marginTop: "-16px", minWidth: 24 }}
                  margin={{ horizontal: "xsmall" }}
                >
                  <Box flex background="#00C781" height="2px" />
                  <svg
                    width="10"
                    height="12"
                    viewBox="0 0 10 12"
                    style={{ flexShrink: 0 }}
                  >
                    <polygon points="0,1 10,6 0,11" fill="#00C781" />
                  </svg>
                </Box>
              )}
            </React.Fragment>
          );
        })}
      </Box>
    );
  }

  return (
    <Box
      className="checkin-flow-timeline"
      pad="medium"
      gap="none"
      style={{ position: "sticky", top: 20 }}
    >
      <Text weight="bold" size="medium" margin={{ bottom: "medium" }}>
        {t("checkinFlow.timeline.guestJourney")}
      </Text>
      {SECTIONS.filter(
        (section) => !(section.hidden && section.hidden(config)),
      ).map((section, index, filteredSections) => {
        const status = section.getStatus(config);
        const isActive = activeSection === section.id;
        const isLast = index === filteredSections.length - 1;

        return (
          <Box key={section.id} direction="row" style={{ minHeight: 72 }}>
            {/* Timeline dot + line */}
            <Box align="center" width="32px" flex={false}>
              <Box
                width="24px"
                height="24px"
                round="full"
                background={status.enabled ? "status-ok" : "light-5"}
                justify="center"
                align="center"
                border={
                  isActive
                    ? { color: "brand", size: "2px" }
                    : {
                        color: status.enabled ? "status-ok" : "light-5",
                        size: "2px",
                      }
                }
                style={{ zIndex: 1, flexShrink: 0 }}
              >
                {status.enabled ? (
                  <Checkmark size="small" color="white" />
                ) : (
                  <Close size="10px" color="dark-5" />
                )}
              </Box>
              {!isLast && (
                <Box
                  width="2px"
                  flex
                  background={status.enabled ? "status-ok" : "light-5"}
                  style={{ minHeight: 32 }}
                />
              )}
            </Box>

            {/* Content */}
            <Box
              pad={{ left: "small", bottom: "small" }}
              flex
              onClick={() => {
                const el = document.getElementById(`section-${section.id}`);
                if (el)
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              style={{ cursor: "pointer" }}
              hoverIndicator={{ color: "light-2" }}
              round="xsmall"
              margin={{ top: "-2px" }}
            >
              <Box direction="row" align="center" gap="xsmall">
                <Text
                  size="xsmall"
                  color={isActive ? "brand" : "dark-4"}
                  weight="bold"
                >
                  {section.label}
                </Text>
              </Box>
              <Text
                size="small"
                weight={isActive ? "bold" : 600}
                color={isActive ? "brand" : "dark-1"}
              >
                {section.title}
              </Text>
              {status.detail && (
                <Text size="xsmall" color="dark-4">
                  {status.detail}
                </Text>
              )}
              {status.steps?.length > 0 && (
                <Box direction="row" wrap gap="2px" margin={{ top: "4px" }}>
                  {status.steps.map((s) => (
                    <Text key={s.key} size="small" title={s.label}>
                      {s.emoji}
                    </Text>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
